import { Component, computed, HostListener, input, output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { GetOpportunityActualValuesResultDto, GetOpportunityTermResultDto } from '@api-proxies';
import { OpportunityTermValue } from '@app/models/OpportunityTermValue';
import { Opportunity } from '@models/Opportunity';
import { OpportunityActualValue } from '@models/OpportunityActualValue';
import { ClientSideGridComponent } from '@shared/grids/client-side-grid/client-side-grid.component';
import { CellValueChangedEvent } from 'ag-grid-enterprise';
import { createValuesGridColumnDefs, validateCellValue, ValidationContext } from './opportunities-values-grid.config';

export interface ValuesGridRow {
    fieldName: string;
    potentialValue: number | undefined;
    actualValue: number | undefined;
    fieldType: 'currency' | 'percentage' | 'number';
    isEditable: boolean;
    fieldKey: string;
}

@Component({
    selector: 'app-opportunities-values-grid',
    templateUrl: './opportunities-values-grid.component.html',
    styleUrls: ['./opportunities-values-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ClientSideGridComponent]
})
export class OpportunitiesValuesGridComponent {
    @ViewChild(ClientSideGridComponent) gridComponent!: ClientSideGridComponent;

    opportunity = input.required<Opportunity>();
    actualValue = input<GetOpportunityActualValuesResultDto>(new OpportunityActualValue());
    termValue = input<GetOpportunityTermResultDto>(new OpportunityTermValue());
    isReadOnly = input<boolean>(false);
    isCreateEstimate = input<boolean>(false);
    baseEstimateExternal = input<boolean>(false);

    valueChange = output<void>();

    rowData = computed<ValuesGridRow[]>(() => {
        const opportunity = this.opportunity();
        const actualValue = this.actualValue();
        const termValue = this.termValue();
        const baseEstimateExt = this.baseEstimateExternal();
        const readonly = this.isReadOnly();

        return [
            {
                fieldName: 'Total Value ($)',
                potentialValue: opportunity.potentialValue,
                actualValue: baseEstimateExt ? actualValue.actualValue : opportunity.actualValue,
                fieldType: 'currency',
                isEditable: !readonly,
                fieldKey: 'potentialValue'
            },
            {
                fieldName: 'Gross Margin (%)',
                potentialValue: opportunity.proposedGrossMarginPercentage,
                actualValue: baseEstimateExt
                    ? actualValue.actualGrossMarginPercentage
                    : opportunity.actualGrossMarginPercentage,
                fieldType: 'percentage',
                isEditable: !readonly,
                fieldKey: 'proposedGrossMarginPercentage'
            },
            {
                fieldName: 'Term Average ($)',
                potentialValue: opportunity.potentialTermAverage,
                actualValue: termValue.avgTotalTerm,
                fieldType: 'currency',
                isEditable: !readonly,
                fieldKey: 'potentialTermAverage'
            },
            {
                fieldName: '# of Terms',
                potentialValue: opportunity.potentialNumberOfTerms,
                actualValue: termValue.totalTerm,
                fieldType: 'number',
                isEditable: !readonly,
                fieldKey: 'potentialNumberOfTerms'
            }
        ];
    });

    validationContext = computed<ValidationContext>(() => ({
        isCreateEstimate: this.isCreateEstimate()
    }));

    columnDefs = computed(() => createValuesGridColumnDefs(this.validationContext()));

    @HostListener('window:resize')
    onWindowResize(): void {
        this.gridComponent?.sizeColumnsToFit({ defaultMinWidth: 100 });
    }

    onCellValueChanged(event: CellValueChangedEvent): void {
        const rowData = event.data as ValuesGridRow;
        const fieldKey = rowData.fieldKey;
        const newValue = event.newValue;

        switch (fieldKey) {
            case 'potentialValue':
                this.opportunity().potentialValue = newValue;
                break;
            case 'proposedGrossMarginPercentage':
                this.opportunity().proposedGrossMarginPercentage =
                    newValue != null && newValue !== '' ? Number(newValue) / 100 : newValue;
                break;
            case 'potentialTermAverage':
                this.opportunity().potentialTermAverage = newValue;
                break;
            case 'potentialNumberOfTerms':
                this.opportunity().potentialNumberOfTerms = newValue;
                break;
        }

        this.valueChange.emit();

        this.gridComponent.refreshCells({ rowNodes: [event.node], force: true });
    }

    validate(): boolean {
        const gridApi = this.gridComponent.getGridApi();
        if (!gridApi) {
            return true;
        }

        const validationContext = this.validationContext();
        let isValid = true;

        gridApi.forEachNode((node) => {
            const rowData = node.data as ValuesGridRow;
            if (!rowData?.isEditable) {
                return;
            }

            const potentialValue = rowData.potentialValue;

            let cellValue: any = potentialValue;
            if (rowData.fieldType === 'percentage' && potentialValue != null) {
                cellValue = potentialValue * 100;
            }

            const potentialCol = gridApi.getColumn('potential');

            if (potentialCol) {
                const validationParams = {
                    data: rowData,
                    value: cellValue,
                    node: node,
                    colDef: potentialCol.getColDef(),
                    column: potentialCol,
                    api: gridApi
                };

                const validationResult = validateCellValue(validationParams, validationContext);
                if (!validationResult.isValid) {
                    isValid = false;
                }
            }
        });

        this.gridComponent.refreshCells({ force: true });

        return isValid;
    }

    clearValidationErrors(): void {
        this.gridComponent.refreshCells({ force: true });
    }
}
